import { FC } from 'react';

import { Button, ChipGroup } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { FilterButtonsContainer, FiltersChipGroup } from './SelectedFilters.styles';

type FilterItemValue = {
    type: 'range' | 'terms' | 'hierarchy';
    fieldName: string;
    term?: string;
};

export type SelectedFilterItem = { value: FilterItemValue; label: string };

type Props = {
    filters: SelectedFilterItem[];
    onRemove: (removedItem: FilterItemValue) => void;
    onRemoveAll: () => void;
    dataTest?: string;
};

const getDataTestValue = (value: string, prefix?: string) => {
    return prefix ? `${prefix}-${value}` : undefined;
};

export const SelectedFilters: FC<Props> = ({ filters, onRemove, onRemoveAll, dataTest }) => {
    const { t } = useTranslations();

    if (!filters?.length) {
        return null;
    }

    const indexedValuesList = filters.map((filter, index) => ({
        id: `${filter.value.fieldName}-${index}`,
        filter,
    }));

    const listOfIds = indexedValuesList.map(({ id }) => id);

    const handleValueChange = (remainingValuesIds: string[]) => {
        const removedItem = indexedValuesList.find((item) => !remainingValuesIds.includes(item.id));
        if (removedItem) {
            onRemove(removedItem.filter.value);
        }
    };

    return (
        <FilterButtonsContainer data-test={dataTest}>
            <FiltersChipGroup
                onValueChange={handleValueChange}
                type="filter"
                value={listOfIds}
                data-test={getDataTestValue('filter-buttons', dataTest)}
            >
                {indexedValuesList.map(({ id, filter }) => {
                    return (
                        <ChipGroup.Item key={id} value={id}>
                            {filter.label}
                        </ChipGroup.Item>
                    );
                })}
            </FiltersChipGroup>
            <Button variant="text" size="sm" onClick={onRemoveAll} data-test={getDataTestValue('remove-all', dataTest)}>
                {t['search.component.selected_filters.clear_filters']}
            </Button>
        </FilterButtonsContainer>
    );
};
